/**
 * Skeleton Loading Components
 *
 * Placeholder UI while content is loading.
 */

interface SkeletonProps {
	width?: string;
	height?: string;
	borderRadius?: string;
	className?: string;
}

export function Skeleton({
	width = '100%',
	height = '1rem',
	borderRadius = '4px',
	className = '',
}: SkeletonProps) {
	return (
		<div
			class={`skeleton ${className}`}
			style={{ width, height, borderRadius }}
		/>
	);
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
	return (
		<div class="skeleton-text">
			{Array.from({ length: lines }).map((_, i) => (
				<Skeleton
					key={i}
					width={i === lines - 1 ? '60%' : '100%'}
					height="0.875rem"
				/>
			))}
		</div>
	);
}

export function SkeletonCard() {
	return (
		<div class="skeleton-card">
			<div class="skeleton-card-header">
				<Skeleton width="60%" height="1.25rem" />
				<Skeleton width="80px" height="1.5rem" borderRadius="9999px" />
			</div>
			<SkeletonText lines={2} />
		</div>
	);
}

export function SkeletonMessage({ isUser = false }: { isUser?: boolean }) {
	return (
		<div class={`skeleton-message ${isUser ? 'skeleton-message-user' : ''}`}>
			<Skeleton width="2rem" height="2rem" borderRadius="50%" />
			<div class="skeleton-message-content">
				<Skeleton width={isUser ? '200px' : '300px'} height="3rem" borderRadius="12px" />
			</div>
		</div>
	);
}

export function DashboardSkeleton() {
	return (
		<div class="dashboard-skeleton">
			{/* Profile + Record */}
			<div class="skel-profile-section">
				<div class="skel-profile-col">
					<Skeleton width="100px" height="0.75rem" className="mb-4" />
					<div class="skel-avatar-card">
						<Skeleton width="100px" height="100px" borderRadius="50%" />
						<Skeleton width="160px" height="0.875rem" />
					</div>
				</div>
				<div class="skel-profile-col">
					<Skeleton width="100px" height="0.75rem" className="mb-4" />
					<Skeleton width="100%" height="2.25rem" borderRadius="2px" />
					<Skeleton width="200px" height="0.75rem" />
					<Skeleton width="100%" height="2.25rem" borderRadius="2px" />
					<Skeleton width="240px" height="0.75rem" />
				</div>
			</div>

			{/* Sessions */}
			<div class="skel-section">
				<div class="skel-section-header">
					<Skeleton width="120px" height="1.5rem" />
					<Skeleton width="80px" height="0.75rem" />
				</div>
				<div class="skel-grid">
					{[1, 2].map(i => (
						<div key={i} class="skel-session-card">
							<div class="skel-row-between">
								<Skeleton width="55%" height="1.125rem" />
								<Skeleton width="70px" height="0.75rem" />
							</div>
							<Skeleton width="80%" height="0.75rem" />
							<Skeleton width="100%" height="0.75rem" />
							<Skeleton width="90%" height="0.75rem" />
							<div class="skel-stats-row">
								<div class="skel-stat-block">
									<Skeleton width="40px" height="1.75rem" />
									<Skeleton width="60px" height="0.625rem" />
								</div>
								<div class="skel-stat-block">
									<Skeleton width="24px" height="1.75rem" />
									<Skeleton width="80px" height="0.625rem" />
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Engagements */}
			<div class="skel-section">
				<div class="skel-section-header">
					<Skeleton width="160px" height="1.5rem" />
					<Skeleton width="120px" height="0.75rem" />
				</div>
				<div class="skel-grid">
					{[1, 2].map(i => (
						<div key={i} class="skel-session-card">
							<div class="skel-row-between">
								<Skeleton width="80px" height="0.75rem" />
								<Skeleton width="50px" height="0.75rem" />
							</div>
							<Skeleton width="40%" height="1.125rem" />
							<Skeleton width="60%" height="0.75rem" />
							<Skeleton width="100%" height="0.75rem" />
							<Skeleton width="95%" height="0.75rem" />
							<Skeleton width="100%" height="0.75rem" />
						</div>
					))}
				</div>
			</div>

			<style>{`
				.dashboard-skeleton {
					padding: 0;
				}

				.skel-profile-section {
					display: flex;
					align-items: flex-start;
					gap: 2.5rem;
					margin-bottom: 1.5rem;
				}

				.skel-profile-col {
					flex: 1;
					min-width: 0;
					display: flex;
					flex-direction: column;
					gap: 0.625rem;
				}

				.skel-avatar-card {
					background: var(--color-bg-elevated);
					border: 1px solid var(--color-border);
					border-radius: 2px;
					padding: 1.5rem;
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 1rem;
				}

				.skel-section {
					margin-top: 1.5rem;
				}

				.skel-section-header {
					display: flex;
					align-items: baseline;
					justify-content: space-between;
					margin-bottom: 1rem;
				}

				.skel-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 0.75rem;
				}

				.skel-session-card {
					background: var(--color-bg-elevated);
					border: 1px solid var(--color-border);
					border-radius: 2px;
					padding: 1rem;
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				}

				.skel-row-between {
					display: flex;
					align-items: center;
					justify-content: space-between;
				}

				.skel-stats-row {
					display: flex;
					gap: 1.5rem;
					margin-top: 0.25rem;
				}

				.skel-stat-block {
					display: flex;
					flex-direction: column;
					gap: 0.25rem;
				}

				@media (max-width: 768px) {
					.skel-profile-section {
						flex-direction: column;
						gap: 1.5rem;
					}

					.skel-grid {
						grid-template-columns: 1fr;
					}
				}
			`}</style>
		</div>
	);
}

export function InterviewSkeleton() {
	return (
		<div class="interview-skeleton">
			<div class="skeleton-messages">
				<SkeletonMessage />
				<SkeletonMessage isUser />
				<SkeletonMessage />
				<SkeletonMessage isUser />
				<SkeletonMessage />
			</div>

			<style>{`
				.interview-skeleton {
					padding: 1.5rem;
					height: 100%;
				}

				.skeleton-messages {
					display: flex;
					flex-direction: column;
					gap: 1rem;
				}

				.skeleton-message {
					display: flex;
					gap: 0.75rem;
					max-width: 80%;
				}

				.skeleton-message-user {
					align-self: flex-end;
					flex-direction: row-reverse;
				}
			`}</style>
		</div>
	);
}

// Global skeleton styles
export function SkeletonStyles() {
	return (
		<style>{`
			.skeleton {
				background: linear-gradient(
					90deg,
					var(--color-bg-hover) 25%,
					var(--color-bg-elevated) 50%,
					var(--color-bg-hover) 75%
				);
				background-size: 200% 100%;
				animation: skeleton-shimmer 1.5s ease-in-out infinite;
			}

			@keyframes skeleton-shimmer {
				0% {
					background-position: 200% 0;
				}
				100% {
					background-position: -200% 0;
				}
			}

			.skeleton-text {
				display: flex;
				flex-direction: column;
				gap: 0.5rem;
			}

			.skeleton-card {
				background: var(--color-bg-elevated);
				border: 1px solid var(--color-border);
				border-radius: 2px;
				padding: 1.25rem;
			}

			.skeleton-card-header {
				display: flex;
				align-items: center;
				justify-content: space-between;
				margin-bottom: 1rem;
			}

			.mt-4 { margin-top: 1rem; }
			.mt-6 { margin-top: 1.5rem; }
			.mt-8 { margin-top: 2rem; }
			.mb-4 { margin-bottom: 1rem; }
		`}</style>
	);
}
